import React, { useState } from 'react';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  VStack,
  HStack,
  Text,
  Badge,
  Image,
  IconButton,
  Center,
  Collapse,
  Button,
  SimpleGrid,
  Divider,
  Code,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import InventorySourceBanner from './InventorySourceBanner';
import { useColors } from './colors';

const PRICE_COLORS = {
  cost: 'green',
  shipping: 'orange',
  handling: 'yellow',
};

const getPriceColor = (key) => {
  const lower = key.toLowerCase();
  if (lower.includes('cost')) return PRICE_COLORS.cost;
  if (lower.includes('shipping')) return PRICE_COLORS.shipping;
  if (lower.includes('handling')) return PRICE_COLORS.handling;
  return 'teal';
};

const formatPriceLabel = (key) =>
  key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');

const DetailRow = ({ label, value, mono = false }) => {
  const { textColor, secondaryTextColor } = useColors();
  return (
    <Flex justify="space-between" align="flex-start" gap={4} py={1.5}>
      <Text fontSize="sm" color={secondaryTextColor} minW="110px" flexShrink={0}>
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="medium"
        color={textColor}
        textAlign="right"
        wordBreak="break-word"
        fontFamily={mono ? 'mono' : undefined}
      >
        {value ?? '—'}
      </Text>
    </Flex>
  );
};

const ResultCard = ({ product, index, imageIndex, onPrevImage, onNextImage, onSelectImage }) => {
  const {
    backgroundCardColor,
    borderColor,
    textColor,
    secondaryTextColor,
    surfaceColor,
    codeBg,
  } = useColors();
  const [showOther, setShowOther] = useState(false);

  const productId =
    product.supplierIdentifier || product.oem || product.partslink || `product-${index}`;
  const images = Array.isArray(product.images) ? product.images : [];
  const currentImage = images.length > 0 ? images[imageIndex || 0] : null;
  const qty = product.quantity ?? 0;
  const qtyColor = qty > 0 ? 'green' : 'red';

  const priceEntries = product.prices
    ? Object.entries(product.prices).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      bg={backgroundCardColor}
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
    >
      <InventorySourceBanner product={product} />

      <Box p={{ base: 4, md: 5 }}>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 220px' }} gap={6}>
          <GridItem>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={textColor} lineHeight="short">
                  {product.description || 'No description'}
                </Text>
                {product.supplierName && (
                  <Text fontSize="sm" color={secondaryTextColor} mt={1}>
                    {product.supplierName}
                  </Text>
                )}
              </Box>

              <HStack spacing={2} flexWrap="wrap">
                <Badge colorScheme={qtyColor} fontSize="sm" px={3} py={1} borderRadius="full">
                  Qty {qty}
                </Badge>
                {product.brand && (
                  <Badge colorScheme="purple" variant="subtle" px={3} py={1} borderRadius="full">
                    {product.brand}
                  </Badge>
                )}
                {product.subWarehouse && (
                  <Badge colorScheme="cyan" variant="outline" px={3} py={1} borderRadius="full">
                    {product.subWarehouse}
                  </Badge>
                )}
                {priceEntries.map(([key, value]) => (
                  <Badge
                    key={key}
                    colorScheme={getPriceColor(key)}
                    variant="subtle"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {formatPriceLabel(key)}: $
                    {typeof value === 'number' ? value.toFixed(2) : value}
                  </Badge>
                ))}
              </HStack>

              <Box bg={surfaceColor} borderRadius="lg" px={4} py={3}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1}>
                  <DetailRow label="Supplier SKU" value={product.supplierIdentifier} mono />
                  <DetailRow label="Partslink" value={product.partslink} mono />
                  <DetailRow label="OEM" value={product.oem} mono />
                  <DetailRow label="Weight" value={product.weight != null ? `${product.weight}` : null} />
                </SimpleGrid>
              </Box>

              {product.other && Object.keys(product.other).length > 0 && (
                <Box>
                  <Button
                    size="sm"
                    variant="ghost"
                    rightIcon={showOther ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setShowOther((v) => !v)}
                    color={secondaryTextColor}
                  >
                    {showOther ? 'Hide' : 'Show'} additional fields ({Object.keys(product.other).length})
                  </Button>
                  <Collapse in={showOther} animateOpacity>
                    <Box
                      mt={2}
                      p={3}
                      bg={codeBg}
                      borderRadius="lg"
                      maxH="240px"
                      overflowY="auto"
                    >
                      <Code
                        display="block"
                        whiteSpace="pre-wrap"
                        fontSize="xs"
                        bg="transparent"
                        color={textColor}
                      >
                        {JSON.stringify(product.other, null, 2)}
                      </Code>
                    </Box>
                  </Collapse>
                </Box>
              )}
            </VStack>
          </GridItem>

          <GridItem>
            <VStack spacing={3} align="stretch">
              <Box
                position="relative"
                w="100%"
                aspectRatio="1"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                overflow="hidden"
                bg={surfaceColor}
              >
                {currentImage ? (
                  <Image
                    src={currentImage}
                    alt={product.description || 'Product'}
                    objectFit="contain"
                    w="100%"
                    h="100%"
                    p={2}
                  />
                ) : (
                  <Center h="100%">
                    <VStack spacing={1}>
                      <Text fontSize="3xl" opacity={0.4}>
                        📷
                      </Text>
                      <Text fontSize="xs" color={secondaryTextColor}>
                        No image
                      </Text>
                    </VStack>
                  </Center>
                )}

                {images.length > 1 && (
                  <>
                    <IconButton
                      icon={<ChevronLeftIcon />}
                      aria-label="Previous image"
                      position="absolute"
                      left={2}
                      top="50%"
                      transform="translateY(-50%)"
                      size="sm"
                      onClick={() => onPrevImage(productId)}
                    />
                    <IconButton
                      icon={<ChevronRightIcon />}
                      aria-label="Next image"
                      position="absolute"
                      right={2}
                      top="50%"
                      transform="translateY(-50%)"
                      size="sm"
                      onClick={() => onNextImage(productId)}
                    />
                  </>
                )}
              </Box>

              {images.length > 1 && (
                <>
                  <Divider />
                  <HStack spacing={2} overflowX="auto" pb={1}>
                    {images.map((image, imgIdx) => (
                      <Image
                        key={imgIdx}
                        src={image}
                        alt={`View ${imgIdx + 1}`}
                        boxSize="52px"
                        objectFit="cover"
                        borderRadius="md"
                        cursor="pointer"
                        border="2px solid"
                        borderColor={imageIndex === imgIdx ? 'blue.400' : borderColor}
                        flexShrink={0}
                        onClick={() => onSelectImage(productId, imgIdx)}
                        opacity={imageIndex === imgIdx ? 1 : 0.75}
                        _hover={{ opacity: 1 }}
                      />
                    ))}
                  </HStack>
                </>
              )}
            </VStack>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
};

export default ResultCard;
